import { useMutation, useQueryClient } from '@tanstack/react-query';
import { maintenanceService, type SendForMaintenanceParams } from '@/services/maintenance';
import { queryKeys } from '@/lib/queryKeys';
import { useNotifications } from '@/stores/useNotificationStore';
import { getErrorMessage } from '@/utils/httpClient';

export const useSendForMaintenance = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: (params: SendForMaintenanceParams) => maintenanceService.sendForMaintenance(params),
    onSuccess: (result) => {
      notifications.success(
        'Sent to maintenance',
        `${result.sentCount} asset${result.sentCount === 1 ? '' : 's'} sent with document ${result.documentNumber}`,
      );
      const warningMessages = Array.isArray(result.warnings)
        ? result.warnings.filter(
            (warning): warning is string =>
              typeof warning === 'string' && warning.trim().length > 0,
          )
        : [];
      if (!result.documentDownloadUrl && warningMessages.length === 0) {
        warningMessages.push(
          'DA Form 2407 was not generated. Maintenance request still completed.',
        );
      }
      if (warningMessages.length > 0) {
        notifications.warning(
          'Sent to maintenance with warnings',
          Array.from(new Set(warningMessages)).join(' '),
        );
      }
      void queryClient.invalidateQueries({ queryKey: ['asset-inventory'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.maintenanceRequests.all });
    },
    onError: (error) => {
      notifications.error('Failed to send to maintenance', getErrorMessage(error));
    },
  });
};
